/**
 * filter-criteria.js
 */

import React from 'react';
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import HiddenInput from 'components/form-inputs/hidden-input';
import { useFilterCriteriaSpecs } from './filter-criteria-specs';


const useStyles = makeStyles(theme => ({
  criteriaList: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(5),
  },
  criteriaRow: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    maxWidth: '100%',
    [theme.breakpoints.up(400)]: {
      maxWidth: '100%',
      flex: 'none'
    }
  },
  criteriaLabel: {
    fontSize: '11px',
    fontWeight: 'bold',
    marginLeft: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  criteriaForm: {
    display: 'flex',
    maxWidth: '100%',
    padding: `${theme.spacing(1)} ${theme.spacing(0.5)} ${theme.spacing(1)} ${theme.spacing(1.5)}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.colors[9],
  },
  criteriaFields: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      flexWrap: 'wrap'
    },
    '& > *': {
      display: 'flex',
    },
    '& .MuiFormControl-root': {
      flex: 1,
      marginRight: theme.spacing(2),
      [theme.breakpoints.up(400)]: {
        width: theme.spacing(25),
      },
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(22),
      }
    },
  },
  dualDropdown: {
    flexDirection: 'column',
    [theme.breakpoints.up(800)]: {
      flexDirection: 'row'
    }
  },
  selectMenu: {
    '& .MuiMenuItem-root': {
      minHeight: theme.spacing(4),
    },
  },
  chip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.light,
    color: 'white',
  },
  checkbox: {
    padding: '6px',
  },
}));


export default function FilterCriteria(props) {
  const classes = useStyles(props);
  const { user } = React.useContext(UserContext);
  const criteriaSpecs = useFilterCriteriaSpecs();
  const { emptyCriteria } = props;
  const { control, setValue} = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: "criteriaList" });
  const criteriaList = useWatch({ control, name: 'criteriaList' }, fields);
  const selectedRecordGroup = parseInt(useWatch({ control, name: 'recordGroup' }));
  const selectedWorkMode = parseInt(useWatch({ control, name: 'workMode' }));


  // Delete criteria that are incompatible with selected WorkMode or RecordGroup
  React.useEffect(() => {
    criteriaList.forEach((rowForm, id) => {
      const { critSpecField1 } = getCriteriaSpec(rowForm.type);
      if (!isCritValid(critSpecField1)) {
        handleDeleteCriteria(id);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkMode, selectedRecordGroup])


  const isCritValid = (crit) => {
    // Reject deployment and vet visit criteria for trainers.
    if (user.role.isTrainer && (crit.group === Types.RecordGroup.DEPLOYMENT || crit.group === Types.RecordGroup.VET_VISIT)) {
      return false;
    }

    // Reject criteria that indicate that that they are not relevant to the user's role.
    // Usage example (in the criteria spec): 
    // 'roles': [Types.Role.HANDLER, Types.Role.SUPERVISOR],
    if (crit.roles && !crit.roles.includes(user.role.id)) {
      return false;
    }
        
    // If a record group is selected and a record group is specified by the criterion,
    // reject if they do not match.
    if (!(
      selectedRecordGroup === Types.RecordGroup.ALL
      || crit.group === Types.RecordGroup.ALL
      || crit.group === selectedRecordGroup)
      ) {
      return false;
    }

    // If a work mode is selected and a work mode is specified by the criterion,
    // reject if they do not match.
    if (!(
      selectedWorkMode === Types.WorkMode.ALL
      || !('workMode' in crit)
      || ('workMode' in crit && (crit.workMode === selectedWorkMode)))
      ) {
      return false;
    }

    // Reject criteria with no values.
    if (crit.values.length === 0) {
      return false;
    }

    return true;
  }


  const getCriteriaSpec = type => {
    const critSpecField1 = criteriaSpecs.find(obj => obj.type === type);
    const critSpecField2 = criteriaSpecs.find(obj => obj.type === critSpecField1.linkedType);
    return { critSpecField1, critSpecField2 };
  };


  const handleCriteriaTypeChange = (type, rowId) => {
    if (type === undefined) return;

    // Update form fields based on selected criteria specification
    const { critSpecField1, critSpecField2 } = getCriteriaSpec(type);
    setValue(`criteriaList[${rowId}].fieldType`, critSpecField1.fieldType);
    setValue(`criteriaList[${rowId}].ddVal`, 
      critSpecField1.values && critSpecField1.values.length > 0 
        ? critSpecField1.values[0].id 
        : emptyCriteria.ddVal);
    setValue(`criteriaList[${rowId}].ddVal2`, emptyCriteria.ddVal2);
    setValue(`criteriaList[${rowId}].type2`, 
      critSpecField2 !== undefined 
        ? critSpecField2.type 
        : emptyCriteria.type2);

    // Override parent form settings if criteria incompatible with current selections
    if ('group' in critSpecField1 
      && (critSpecField1.group !== selectedRecordGroup)
      && critSpecField1.group !== Types.RecordGroup.ALL
    ) {
      setValue('recordGroup', critSpecField1.group);
    }
    if ('workMode' in critSpecField1 && (critSpecField1.workMode !== selectedWorkMode)) {
      setValue('workMode', critSpecField1.workMode);
    }
  };


  const handleDropDown1Change = (val, rowId) => {
    setValue(`criteriaList[${rowId}].ddVal`, val);
    setValue(`criteriaList[${rowId}].ddVal2`, emptyCriteria.ddVal2);
  };


  const handleDropDown2Change = (val, rowId, type) => {
    setValue(`criteriaList[${rowId}].type2`, type);
  };


  const handleDeleteCriteria = (rowId) => {
      remove(rowId);
  };


  const renderCriteriaTypesSelector = () => {
    // NOTE: If the user changes the recordGroup or workMode then it's possible that
    // the currently selected criteria will no longer be valid. This will cause an
    // out-of-range warning for the select in the console. Other than the warning
    // message it seems to work fine. I'm not sure how to fix this.

    const menuItems = [];
    Object.values(Types.RecordGroup).forEach(group => {
      criteriaSpecs.filter(crit => crit.group === group && isCritValid(crit)).forEach((crit, id) => {
        if (!menuItems.find(mi => mi.key === `${crit.group}`)) {
          menuItems.push(
            <ListSubheader key={crit.group}>
              {crit.groupTitle}
            </ListSubheader>
          );
        }

        menuItems.push(
          <MenuItem
            key={crit.type}
            value={crit.type}
          >
            {crit.title}
          </MenuItem>
        );
      });
    });

    return menuItems;
  };


  return (
    <div className={classes.criteriaList}>

      {fields.map((formRow, rowId) => {
        const formData = criteriaList[rowId] || formRow;
        const fieldType = parseInt(formData.fieldType);
        const { critSpecField1, critSpecField2 } = getCriteriaSpec(formData.type);
        const isDualDropdown = critSpecField2 !== undefined;

        // don't render non-empty fields that are not valid for the current context
        if (!(critSpecField1.isEmpty || isCritValid(critSpecField1))) {
          return null;
        }

        return (
          <div key={formRow.id} style={{ display: 'flex' }}>
            <div className={classes.criteriaRow}>
              <Typography className={classes.criteriaLabel}>
                FILTER {rowId + 1}
              </Typography>

              <div className={classes.criteriaForm}>
                <div className={classes.criteriaFields}>

                  {/* Criteria type selector */}
                  <div>
                    {/* The field type value for the first dropdown is stored in a hidden field */}
                    <HiddenInput
                      name={`criteriaList[${rowId}].fieldType`}
                      defaultValue={formRow.fieldType}
                    />

                    <FormControl
                      variant="outlined"
                      margin="dense"
                      size='small'
                    >
                      <InputLabel>Find Records With</InputLabel>
                      <Controller
                        name={`criteriaList[${rowId}].type`}
                        control={control}
                        defaultValue={formRow.type}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Find Records With"
                            MenuProps={{classes: {list: classes.selectMenu} }}
                            onChange={(e) => {
                              if (e.target.value !== undefined) {
                                handleCriteriaTypeChange(e.target.value, rowId);
                                field.onChange(e.target.value);
                              }
                            }}
                          >
                            {renderCriteriaTypesSelector()}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </div>


                  {/* The selected criteria type requires a dropdown field */}
                  <div 
                    style={{ display: fieldType === Types.FilterCriteriaField.DROPDOWN ? 'flex' : 'none' }}
                    className={classes.dualDropdown}
                  >
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      size='small'
                    >
                      <InputLabel>Set To</InputLabel>
                      <Controller
                        name={`criteriaList[${rowId}].ddVal`}
                        control={control}
                        defaultValue={formRow.ddVal}
                        render={({ field }) => fieldType === Types.FilterCriteriaField.DROPDOWN ? (
                          <Select
                            {...field}
                            label="Set To"
                            MenuProps={{classes: {list: classes.selectMenu} }}
                            onChange={(e) => {
                              if (isDualDropdown) handleDropDown1Change(e.target.value, rowId);
                              field.onChange(e.target.value);
                            }}
                          >
                            {critSpecField1.values.map(data =>
                              <MenuItem key={data.id} value={data.id}>
                                {data.val}
                              </MenuItem>
                            )}
                          </Select>
                        ) : null}
                      />
                    </FormControl>

                    {/* The selected criteria type requires an extra dropdown field and a hidden field */}
                    <div style={{ display: isDualDropdown ? 'flex' : 'none' }}>
                      {/* The field type value for the second dropdown is stored in a hidden field */}
                      <HiddenInput
                        name={`criteriaList[${rowId}].type2`}
                        defaultValue={formRow.type2}
                      />

                      {/* Secondary dropdown */}
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        size='small'
                      >
                        <InputLabel>{isDualDropdown && critSpecField2.title}</InputLabel>
                        <Controller
                          name={`criteriaList[${rowId}].ddVal2`}
                          control={control}
                          defaultValue={formRow.ddVal2}
                          render={({ field }) => {
                            return isDualDropdown ? (
                              <Select
                                {...field}
                                label={isDualDropdown && critSpecField2.title}
                                MenuProps={{classes: {list: classes.selectMenu} }}
                                onChange={(e) => {
                                  handleDropDown2Change(e.target.value, rowId, critSpecField2.type);
                                  field.onChange(e.target.value);
                                }}
                              >
                                {(() => {
                                  const isValid = (typeof formData.ddVal !== 'undefined') &&
                                    (formData.ddVal <= critSpecField2.values.length - 1);
                                  return (isValid &&
                                    critSpecField2.values[formData.ddVal].map(({val, id}) =>
                                      <MenuItem key={id} value={id}>
                                        {val}
                                      </MenuItem>
                                  ));
                                })()}
                              </Select>
                            ) : null;
                          }}
                        />
                      </FormControl>
                    </div>
                  </div>


                  {/* The selected criteria type requires a text field */}
                  <div style={{ display: fieldType === Types.FilterCriteriaField.TEXT ? 'flex' : 'none' }}>
                    <Controller
                      control={control}
                      name={`criteriaList[${rowId}].textVal`}
                      defaultValue={formRow.textVal}
                      render={({ field }) => 
                        <TextField
                          {...field}
                          size='small'
                          label="Containing"
                          variant="outlined"
                          margin="dense"
                        />}
                    />
                  </div>


                  {/* The selected criteria type requires a tag selection field */}
                  <div style={{ display: fieldType === Types.FilterCriteriaField.TAG ? 'flex' : 'none' }}>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      size='small'
                    >
                      <InputLabel>{critSpecField1.title}</InputLabel>
                      <Controller
                        name={`criteriaList[${rowId}].tagVals`}
                        control={control}
                        defaultValue={formRow.tagVals}
                        render={({ field }) => fieldType === Types.FilterCriteriaField.TAG ? (
                          <Select
                            {...field}
                            label={critSpecField1.title}
                            multiple
                            MenuProps={{classes: {list: classes.selectMenu} }}
                            renderValue={selected => (
                              <div style={{ display: 'flex', flexWrap: 'wrap', margin: -4 }}>
                                {selected.map(id => {
                                  // Special handling for Any Tag option
                                  if (id === 'any') {
                                    return (
                                      <Chip
                                        key="any"
                                        label="<ANY TAG>"
                                        size="small"
                                        className={classes.chip}
                                      />
                                    );
                                  }
                                  const tag = critSpecField1.values.find(obj => obj.id === +id);
                                  return (
                                    <Chip
                                      key={tag?.id}
                                      label={tag?.val}
                                      size="small"
                                      className={classes.chip}
                                    />
                                  )
                                })}
                              </div>
                            )}
                            onChange={(e) => {
                              const selectedValues = e.target.value;
                              // If Any Tag is selected, clear other selections
                              if (selectedValues.includes('any')) {
                                field.onChange(['any']);
                              } 
                              // If another tag is selected while Any Tag is selected, remove Any Tag
                              else if (formData.tagVals.includes('any')) {
                                field.onChange(selectedValues.filter(val => val !== 'any'));
                              }
                              // Normal multi-select behavior
                              else {
                                field.onChange(selectedValues);
                              }
                            }}
                          >
                            {critSpecField1.values.map(data =>
                              <MenuItem key={data.id} value={data.id}>
                                <Checkbox
                                  color="primary"
                                  size="small"
                                  className={classes.checkbox}
                                  checked={formData.tagVals.indexOf(data.id) > -1}
                                  disabled={formData.tagVals.includes('any')} // Disable other options when Any Tag is selected
                                />
                                {data.val}
                              </MenuItem>
                            )}
                          </Select>
                        ) : null}
                      />
                    </FormControl>
                  </div>

                </div>


                {/* Delete criteria button */}
                <div style={{ display: 'flex' }}>
                  <IconButton
                    onClick={() => {
                      handleDeleteCriteria(rowId);
                    }}
                    size="large">
                    <Icon type="delete" size="small" />
                  </IconButton>
                </div>

              </div>
            </div>
          </div>
        );}
      )}

      <div>
        {criteriaList.length === 0 &&
          <Typography variant='subtext-large' sx={{ mb: 1 }}>
            Add an advanced filter to refine your search.
            Filter by Arrests, Requesting Agency, Odor Type,
            Comment, etc.
          </Typography>
        }
        <Button
          color="primary"
          startIcon={<Icon type="filter-plus" />}
          onClick={() => {
            append(emptyCriteria);
          }}
        >
          Add Filter
        </Button>
      </div>

    </div>
  );

}
